import { Company } from '@apis/Customers/model';
import { QueryExpr } from '@apis/Resources';
import { BaseResource } from '@apis/Resources/model';
import { GridChartSettings } from '@root/Components/Charts/GridChart';
import { ICompanyContextToken } from '@root/Services/Customers/CompanyContext';
import { AppFeatureNames } from '@root/Services/Customers/CompanyFeatureService';
import { IUserFeatureAccessLookup, UserFeatureAccessService } from '@root/Services/Customers/UserFeatureAccessService';
import { NamedFormats } from '@root/Services/FormatService';
import { QueryDatasource, QueryDatasourceMetadata } from '@root/Services/Query/QueryDatasource';
import { exprBuilder, SchemaService } from '@root/Services/QueryExpr';
import { IdleResourcesService } from '@root/Services/Resources/IdleResourcesService';
import { ResourceQueryDatasourceFactory } from '@root/Services/Resources/ResourceQueryDatasourceFactory';
import { ResourceQueryService } from '@root/Services/Resources/ResourceService';
import { BorderAll, CircleNumber1, Eye } from 'tabler-icons-react';
import { inject, injectable } from 'tsyringe';
import { describeDatasource } from '../../Components/DatasourceDescriptor';
import { BaseDatasourcePresetsBuilder, IDatasourcePresetsBuilderFactory, INotificationPresetItem, PresetFormHelpers } from './BasePresets';

class ResourceDatasourcePresetsBuilder extends BaseDatasourcePresetsBuilder {
    public constructor(
        queryDatasource: QueryDatasource,
        schemaSvc: SchemaService,
        private readonly featureAccessLookup: IUserFeatureAccessLookup,
        private readonly idleResourceQueries: Map<string, QueryExpr>
    ) {
        super(queryDatasource, schemaSvc);
    }

    public getPresets() {
        const { name, icon: Icon } = describeDatasource(this.baseQueryDatasource);
        return this.createDatasourcePresets(
            this.baseQueryDatasource.title ?? name,
            <Icon />,
            [
                ...this.buildExpiringRiAlerts(),
                this.buildExpiringSpAlert(),
                ...this.buildIdleResourceAlerts(),
                this.buildTagHealthAlert(),
                this.buildCustomResourceAlert(),
            ],
            [this.buildWeeklyTagHealthReport(), this.buildCustomResourceReport()]
        );
    }

    // #region Alerts

    private buildExpiringRiAlerts(): INotificationPresetItem<any>[] {
        const riTypes = [
            {
                type: 'EC2 Reserved',
                suffix: 'ec2',
                label: 'EC2',
                idFld: 'ReservedInstancesId',
                stateFld: 'State.Value',
                typeFld: 'InstanceType.Value',
                ctFld: 'InstanceCount',
            },
            {
                type: 'RDS Reserved',
                suffix: 'rds',
                label: 'RDS',
                idFld: 'ReservedDBInstanceArn',
                stateFld: 'State',
                typeFld: 'DBInstanceClass',
                ctFld: 'DBInstanceCount',
            },
        ];

        return riTypes.map(({ type, suffix, label, idFld, stateFld, typeFld, ctFld }) => {
            type RiParams = { pExpiration: Date; pDaysPrior: number; Region: string; End: Date; endDt: Date };
            const xb = this.createExprBuilder<RiParams & { end: Date }>();
            const [idExpr, instCtExpr, typeExpr, regExpr, endExpr] = xb.select({
                ID: xb.model[idFld],
                '# Instances': xb.sum(xb.model[ctFld] as unknown as number),
                Type: xb.model[typeFld],
                Region: xb.model.Region,
                End: xb.model.End,
            });

            return this.buildPreset({
                idParts: ['expiring-ri-' + suffix],
                label: `Expiring ${label} Reservations`,
                description: `Notify in advance when ${label} reservations are near their expiration date.`,
                icon: <BorderAll />,
                initialize: (data) =>
                    this.mergeConfig(
                        {
                            Title: `${label} Reservations Expiring Soon`,
                            Description: `Some ${label} reservations will expire soon. Please review the details.`,
                            ComponentConfig: {
                                ...this.createCustomChartConfig('grid'),
                                settings: {
                                    columns: [
                                        { id: 'id', select: idExpr, type: 'string', formatter: 'string' },
                                        { id: 'instCt', select: instCtExpr, type: 'number', formatter: 'int' },
                                        { id: 'type', select: typeExpr, type: 'string', formatter: 'string' },
                                        { id: 'region', select: regExpr, type: 'string', formatter: 'string' },
                                        { id: 'endDt', select: endExpr, type: 'date', formatter: 'short-date' },
                                    ],
                                    state: {
                                        columns: [
                                            { id: 'id', width: 250 },
                                            { id: 'instCt', width: 100 },
                                            { id: 'type', width: 140 },
                                            { id: 'region', width: 100 },
                                            { id: 'endDt', width: 110 },
                                        ],
                                        filters: [],
                                        sort: [{ Expr: xb.resolve(xb.model.End), Direction: 'Asc' }],
                                        groupBy: [],
                                    },
                                    allowFilter: false,
                                } as GridChartSettings,
                                filters: [xb.resolve(xb.model[stateFld].eq(['active'])), xb.resolve(xb.model.ResourceType!.eq([type]))],
                            },
                            Trigger: this.defaultAlertTrigger('grid-alert', {
                                filter: [xb.resolve(xb.model.endDt.onOrBefore(xb.model.pExpiration))],
                            }),
                            QueryOptions: {
                                Parameters: {
                                    pDaysPrior: { Value: 14 },
                                    pExpiration: xb.resolve(xb.addDate(xb.currentDate(), xb.model.pDaysPrior, 'day')),
                                },
                                DatasourceName: this.baseQueryDatasource.name,
                                Filters: [],
                            },
                        },
                        data
                    ),
                getForm: (data, h) => this.standardAlertForm(data, h),
            });
        });
    }

    private buildExpiringSpAlert(): INotificationPresetItem<any> {
        const xb = this.createExprBuilder<{
            'State.Value': string;
            SavingsPlanArn: string;
            End: Date;
            Commitment: string;
            TermDurationInSeconds: number;
            pExpiration: Date;
            pDaysPrior: number;
        }>();
        const [sp, spType, expires, value] = this.selectExprs({
            'Savings Plan': xb.model.SavingsPlanArn,
            Type: xb.model['SavingsPlanType.Value'],
            Expiration: xb.model.End,
            Value: xb.sum(
                xb.convert<number>(xb.model.Commitment, 'number').times(xb.model.TermDurationInSeconds.dividedBy(60 * 60) as unknown as number)
            ),
        });

        return this.buildPreset({
            idParts: ['expiring-sp'],
            label: 'Expiring Savings Plans',
            description: 'Notify in advance when savings plans are near their expiration date.',
            icon: <BorderAll />,
            initialize: (data) =>
                this.mergeConfig(
                    {
                        Title: 'Savings Plan Expiration',
                        Description: 'Some savings plan will expire soon. Please review the details.',
                        ComponentConfig: {
                            ...this.createCustomChartConfig('grid'),
                            settings: {
                                columns: [
                                    { id: 'spid', select: sp, type: 'string', formatter: 'string' },
                                    { id: 'sptype', select: spType, type: 'string', formatter: 'string' },
                                    { id: 'end', select: expires, type: 'date', formatter: 'short-date' },
                                    { id: 'value', select: value, type: 'number', formatter: 'currency-2dec' },
                                ],
                                state: {
                                    columns: [
                                        { id: 'spid', width: 250 },
                                        { id: 'sptype', width: 130 },
                                        { id: 'end', width: 110 },
                                        { id: 'value', width: 110 },
                                    ],
                                    filters: [],
                                    sort: [{ Expr: xb.resolve(xb.model.End), Direction: 'Asc' }],
                                    groupBy: [],
                                },
                                allowFilter: false,
                            } as GridChartSettings,
                        },
                        Trigger: {
                            TriggerType: 'Threshold',
                            Threshold: {
                                ThresholdCondition: xb.resolve(
                                    xb.and(xb.model.End.before(xb.model.pExpiration), xb.model['State.Value'].eq('active'))
                                ),
                                ThrottlePeriod: 'Daily',
                            },
                        },
                        QueryOptions: {
                            Parameters: {
                                pDaysPrior: { Value: 14 },
                                pExpiration: xb.resolve(xb.addDate(xb.currentDate(), xb.model.pDaysPrior, 'day')),
                            },
                            DatasourceName: this.baseQueryDatasource.name,
                            Filters: [],
                        },
                    },
                    data
                ),
            getForm: (data, h) => this.standardAlertForm(data, h),
        });
    }

    private buildIdleResourceAlerts(): INotificationPresetItem<any>[] {
        if (!this.featureAccessLookup.checkAccessByName(AppFeatureNames.IdleResources)) {
            return [];
        }

        const xb = this.createExprBuilder<BaseResource>();
        const resourceTypes = [
            { type: 'EC2', indexedType: ['EC2 On Demand'] },
            { type: 'RDS', indexedType: ['RDS'] },
            { type: 'EMR', indexedType: ['EMR'] },
            { type: 'ELB', indexedType: ['ELBV1', 'ELBV2'] },
            { type: 'Redshift', indexedType: ['Redshift'] },
        ];

        return resourceTypes
            .map(({ type, indexedType }) => ({ type, indexedType, crit: this.idleResourceQueries.get(type) }))
            .filter(({ crit }) => !!crit)
            .map(({ type, indexedType, crit }) => {
                const idleExpr = xb.fromExpr<boolean>(crit!);
                const [annCost, idle, all, idlePct] = xb.select({
                    annCost: xb.aggIf(idleExpr, xb.sum(xb.model.ExtendedAnnualizedCost!)),
                    idleCt: xb.count(idleExpr),
                    all: xb.count(),
                    idlePct: xb.op('percent', idleExpr),
                });

                return this.buildPreset({
                    idParts: ['idle-' + type + '-count-alert'],
                    label: `Idle ${type} Above Threshold`,
                    description: `Notify when the cost or number of idle ${type} is above a configurable threshold.`,
                    icon: <CircleNumber1 />,
                    initialize: (data) =>
                        this.mergeConfig(
                            {
                                Title: `Idle ${type} Alert`,
                                Description: `Alert threshold reached for idle ${type} resources.\nConsider terminating, stopping, or scheduling.`,
                                ComponentConfig: {
                                    ...this.createCustomChartConfig('kpi'),
                                    values: [annCost, idle, all, idlePct],
                                    groups: [],
                                    settings: {
                                        format: ['money-with-two-decimals', 'int', 'int', 'percent'],
                                        labels: [`Idle ${type} $/yr`, `# Idle ${type}`, `# ${type} Total`, `% Idle`],
                                    },
                                },
                                Trigger: {
                                    ...this.defaultAlertTrigger(),
                                    Threshold: {
                                        PresentationOptions: data.Trigger?.PresentationOptions ?? {
                                            settings: [
                                                {
                                                    optionId: 'kpi-alert',
                                                    model: {
                                                        conditions: [
                                                            { left: 'idleCt', comparitor: 'gt', rightType: 'value', rightValue: 10 },
                                                            { left: 'annCost', comparitor: 'gte', rightType: 'value', rightValue: 10000 },
                                                        ],
                                                    },
                                                },
                                            ],
                                        },
                                    },
                                },
                                QueryOptions: {
                                    Parameters: {},
                                    DatasourceName: this.baseQueryDatasource.name,
                                    Filters: [xb.resolve(xb.model.ResourceType!.eq(indexedType))],
                                },
                            },
                            data
                        ),
                    getForm: (data, h) => this.standardAlertForm(data, h, { collapsed: true }),
                });
            });
    }

    private buildTagHealthAlert(): INotificationPresetItem<any> {
        const xb = this.createExprBuilder<{ ['Health %']: number }>();
        const { gridConfig } = this.createTagHealthGrid();
        const label = 'Overall Tag Health Below Threshold';
        const description = 'Notify when the tag health of resources drops below a threshold.';

        const id = this.createId('tag-health-report');
        const defaultConfig = {
            Title: label,
            Description: description,
            Trigger: {
                ...this.defaultAlertTrigger(),
                Threshold: {
                    PresentationOptions: {
                        settings: [
                            {
                                optionId: 'grid-alert',
                                model: {
                                    filter: [xb.resolve(xb.model['Health %']!.lt(0.6))],
                                },
                            },
                        ],
                    },
                },
            },
            QueryOptions: this.defaultQueryOptions(this.baseQueryDatasource.name),
        };
        const behavior = this.initializeBahavior(id, gridConfig, {}, defaultConfig).form(this.createStandardAlertBehavior(undefined, true));

        return { id, label, description, icon: <BorderAll />, behavior };
    }

    private buildCustomResourceAlert(): INotificationPresetItem<any> {
        const dimExpr = this.baseQueryDatasource.getDefaultGroup();
        const metricExpr = this.baseQueryDatasource.getDefaultValue();

        return this.buildCustomAlertPreset((data) =>
            this.mergeConfig(
                {
                    ComponentConfig: this.createCustomChartConfig('bar', dimExpr, metricExpr),
                    QueryOptions: this.defaultQueryOptions(this.baseQueryDatasource.name),
                    Trigger: this.defaultAlertTrigger(),
                },
                data
            )
        );
    }

    // #endregion

    // #region Reports

    private buildWeeklyTagHealthReport(): INotificationPresetItem<any> {
        const id = this.createId('tag-health-alert');
        const { gridConfig, inputs } = this.createTagHealthGrid();
        const label = 'Weekly Tag Health Report';
        const description = 'Weekly report on the tag health of your resources.';
        const defaultConfig = {
            Title: 'Weekly Tag Health Report',
            Description: description,
            Trigger: this.defaultSchedTrigger('Weekly'),
            QueryOptions: this.defaultQueryOptions(this.baseQueryDatasource.name),
        };
        const behavior = this.initializeBahavior(id, gridConfig, {}, defaultConfig).standardReport(() => ({
            dataOptions: (data) => {
                const pivotPicker = inputs(data.ComponentConfig.settings);
                return this.createSectionItem('Data Options', pivotPicker);
            },
        }));

        return { id, label, description, icon: <BorderAll />, behavior };
    }

    private buildCustomResourceReport(): INotificationPresetItem<any> {
        const dimExpr = this.baseQueryDatasource.getDefaultGroup();
        const metricExpr = this.baseQueryDatasource.getDefaultValue();

        return this.buildCustomReportPreset((data) =>
            this.mergeConfig(
                {
                    ComponentConfig: this.createCustomChartConfig('bar', dimExpr, metricExpr),
                    QueryOptions: this.defaultQueryOptions(this.baseQueryDatasource.name),
                    Trigger: this.defaultSchedTrigger('Weekly'),
                },
                data
            )
        );
    }

    // #endregion

    // #region Shared helpers

    private createTagHealthGrid() {
        const dimExpr = this.baseQueryDatasource.getDefaultGroup();
        const xb = this.createExprBuilder<BaseResource>();
        const scoreCols = this.selectExprs({
            'Coverage %': xb.avg(xb.model.CoverageScore!),
            'Clarity %': xb.avg(xb.model.ClarityScore!),
            'Compliance %': xb.avg(xb.model.ComplianceScore!),
            'Health %': xb.avg(xb.model.TagHealthScore!),
        });

        const gridConfig = this.createGridChartConfig(
            { id: 'pivot', select: dimExpr, type: 'string', formatter: 'string', width: 225, sortDir: 'Asc' },
            ...scoreCols.map((c) => ({ id: c.Alias!, select: c, type: 'number', formatter: 'percent' as NamedFormats, width: 125 }))
        );

        const inputs = (data: GridChartSettings) => {
            const updateAlias = (fld: QueryExpr) => {
                data.columns[0].select!.Alias = this.schemaCtx.queryDescriptorSvc.getName(fld);
            };
            return this.createInput<GridChartSettings>()
                .type('string-expr')
                .options({
                    label: 'View By',
                    schemaCtx: this.schemaCtx,
                    acceptConstant: false,
                    icon: <Eye />,
                    operations: [],
                })
                .map((d) => d.columns[0].select!.Expr! as QueryExpr)
                .bind(data, updateAlias);
        };
        return { gridConfig, inputs };
    }

    private createExprBuilder<TParams>() {
        type Model = BaseResource & TParams;
        const { builder } = exprBuilder<Model>();
        return builder;
    }

    // #endregion
}

@injectable()
export class ResourceDatasourcePresetsBuilderFactory implements IDatasourcePresetsBuilderFactory {
    public constructor(
        @inject(ResourceQueryDatasourceFactory) private readonly datasourceFactory: ResourceQueryDatasourceFactory,
        @inject(ResourceQueryService) private readonly resourceQuerySvc: ResourceQueryService,
        @inject(ICompanyContextToken) private readonly company: Company,
        @inject(UserFeatureAccessService) private readonly featureAccessSvc: UserFeatureAccessService,
        @inject(IdleResourcesService) private readonly idleResourcesSvc: IdleResourcesService
    ) {}

    public getSupportedDatasources(): QueryDatasourceMetadata[] {
        return [this.datasourceFactory.getMetadata()];
    }
    public async get(datasourceName: string) {
        const datasource = await this.getDatasourceByName(datasourceName);
        const [types, featureAccessLookup, idleQueries] = await Promise.all([
            datasource.schema.getSchema(),
            this.featureAccessSvc.getAccessLookup(this.company),
            this.idleResourcesSvc.getIdleQuery(),
        ]);
        const schemaSvc = SchemaService.create(types);

        return new ResourceDatasourcePresetsBuilder(datasource, schemaSvc, featureAccessLookup, idleQueries);
    }

    private async getDatasourceByName(name: string) {
        switch (name.toLowerCase()) {
            case 'resources':
                return await this.datasourceFactory.getDatasource((q) => this.resourceQuerySvc.query(q, this.company.Id ?? 0));
            default:
                throw new Error(`Unsupported datasource ${name}`);
        }
    }
}
